import type { AnyRouteMatch } from "@tanstack/router-core";

import type { PublicDirectory, PublicPlace, PublicService, TrainerPageConfig } from "@/features/site/types";
import type { ShopProduct } from "@/features/shop/types";
import { localizePath } from "@/lib/locale";
import { breadcrumbJsonLd, jsonLdScript, pageMeta, type HeadMeta, type HeadScript } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";
import { getLocale, getUrlOrigin } from "@/paraglide/runtime.js";

/** The business's front page, the one that carries its structured data. */
const HOME_ROUTE_ID = "/(public)/$trainerSlug/";
const SERVICE_ROUTE_ID = "/(public)/$trainerSlug/services/$slug/";
const SERVICES_ROUTE_ID = "/(public)/$trainerSlug/services/";

/** Roughly what a search result shows of a description before it cuts it off. */
const DESCRIPTION_LENGTH = 155;

type Section = { name: () => string; path: string; description: (inputs: { name: string }) => string };

/**
 * The section each page below the front page is, by route. Read off the route rather than the
 * page's loader: the layout's `head` is handed its matches before the pages' loaders have run.
 */
const SECTIONS: Record<string, Section> = {
  [SERVICES_ROUTE_ID]: {
    name: m.landing_nav_services,
    path: "/services",
    description: m.seo_trainer_services_description,
  },
  [SERVICE_ROUTE_ID]: {
    name: m.landing_nav_services,
    path: "/services",
    description: m.seo_trainer_services_description,
  },
  "/(public)/$trainerSlug/coaches/": {
    name: m.landing_nav_coaches,
    path: "/coaches",
    description: m.seo_trainer_coaches_description,
  },
  "/(public)/$trainerSlug/pricing/": {
    name: m.landing_nav_pricing,
    path: "/pricing",
    description: m.seo_trainer_pricing_description,
  },
  "/(public)/$trainerSlug/contact/": {
    name: m.landing_nav_contact,
    path: "/contact",
    description: m.seo_trainer_contact_description,
  },
  "/(public)/$trainerSlug/shop/": { name: m.shop_title, path: "/shop", description: m.seo_trainer_shop_description },
  "/(public)/$trainerSlug/shop/$productId/": {
    name: m.shop_title,
    path: "/shop",
    description: m.seo_trainer_shop_description,
  },
};

/** What the `/$trainerSlug` loader hands its pages: null when the server could not reach the API. */
export type TrainerLoaderData = {
  page: TrainerPageConfig;
  business: { name: string; logoUrl: string | null };
  directory: PublicDirectory;
  crumb: string;
} | null;

/** A page of the business's site, as an absolute address in the language being read. */
const siteUrl = (slug: string, path = "") => `${getUrlOrigin()}${localizePath(`/${slug}${path}`, getLocale())}`;

/** A business's Markdown as a line of plain text, cut at a word to fit a description. */
export const plainExcerpt = (markdown: string, length = DESCRIPTION_LENGTH): string => {
  const text = markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+\.)\s+/gm, "")
    .replace(/[*_`~]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= length) return text;
  const cut = text.slice(0, length - 1);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(/[\s.,;:]+$/, "")}…`;
};

/** Amounts are in the currency's minor units; a price in structured data is the whole amount. */
const wholeAmount = (amount: number) => (amount / 100).toFixed(2);

/**
 * A service's price as a schema.org offer. A "from" price is a minimum rather than the price, and
 * a service with no price set has no offer to describe.
 */
const serviceOffer = (service: PublicService, currency: string, url: string) => {
  if (service.priceAmount === null) return undefined;
  const price = wholeAmount(service.priceAmount);
  return {
    "@type": "Offer",
    url,
    priceCurrency: currency,
    ...(service.priceFrom
      ? { priceSpecification: { "@type": "PriceSpecification", minPrice: price, priceCurrency: currency } }
      : { price }),
  };
};

/** Where the business is, from its main place, or the address it typed when it has no such place. */
const postalAddress = (place: PublicPlace | undefined, typed: string) => {
  const streetAddress = place?.address || place?.gym?.address || typed;
  const addressLocality = place?.city || place?.gym?.city;
  const postalCode = place?.postalCode;
  if (!streetAddress && !addressLocality) return undefined;
  return {
    "@type": "PostalAddress",
    ...(streetAddress ? { streetAddress } : {}),
    ...(addressLocality ? { addressLocality } : {}),
    ...(postalCode ? { postalCode } : {}),
  };
};

const businessJsonLd = ({
  page,
  business,
  directory,
  description,
  image,
}: {
  page: TrainerPageConfig;
  business: { name: string; logoUrl: string | null };
  directory: PublicDirectory;
  description: string;
  image: string | null;
}) => {
  const place = directory.places?.find((candidate) => candidate.main) ?? directory.places?.[0];
  const { services } = directory;
  const { currency } = directory.business;

  return jsonLdScript({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: business.name,
    url: siteUrl(page.slug),
    description,
    image: image ?? undefined,
    logo: business.logoUrl ?? undefined,
    telephone: page.phone || undefined,
    email: page.email || undefined,
    address: postalAddress(place, page.address),
    hasOfferCatalog: services.length
      ? {
          "@type": "OfferCatalog",
          name: m.landing_nav_services(),
          itemListElement: services.map((service) => {
            const url = siteUrl(page.slug, `/services/${service.id}`);
            return {
              "@type": "Offer",
              ...serviceOffer(service, currency, url),
              itemOffered: { "@type": "Service", name: service.name, url },
            };
          }),
        }
      : undefined,
  });
};

const serviceJsonLd = ({
  service,
  slug,
  businessName,
  currency,
  description,
}: {
  service: PublicService;
  slug: string;
  businessName: string;
  currency: string;
  description: string;
}) => {
  const url = siteUrl(slug, `/services/${service.id}`);
  return jsonLdScript({
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description,
    url,
    provider: { "@type": "LocalBusiness", name: businessName, url: siteUrl(slug) },
    offers: serviceOffer(service, currency, url),
  });
};

/**
 * A business's pages are titled with its own name, never the platform's (ADR 0016): its name on
 * the front page, "<section> · <name>" below it, and a service's own name on its page. The
 * description is the business's own words when it wrote them. Link previews show its cover photo,
 * else its logo, else the platform's card. Structured data describes the business on its front
 * page, the service on a service's page, and where a page sits under the business everywhere else.
 */
export const trainerPageHead = ({
  loaderData,
  matches,
}: {
  loaderData: TrainerLoaderData | undefined;
  matches: AnyRouteMatch[];
}): { meta: HeadMeta[]; scripts: HeadScript[] } => {
  // The API could not be reached on the server; the browser loads the page and its head again.
  if (!loaderData) return { meta: [], scripts: [] };

  const { page, business, directory } = loaderData;
  const leaf = matches.at(-1);
  const isHome = leaf?.routeId === HOME_ROUTE_ID;
  const section = leaf ? SECTIONS[leaf.routeId] : undefined;
  const service =
    leaf?.routeId === SERVICE_ROUTE_ID
      ? directory.services.find((candidate) => candidate.id === (leaf.params as { slug?: string }).slug)
      : undefined;
  const { name } = business;
  const description = service
    ? plainExcerpt(service.description) || m.seo_service_description({ service: service.name, name })
    : section
      ? section.description({ name })
      : page.lede || m.seo_trainer_description({ name });
  const title = service?.name ?? section?.name();
  const image = page.coverUrl ?? business.logoUrl;
  const previewImage = image ?? `${getUrlOrigin()}/assets/og-image.png`;
  const crumbs = [{ name, url: siteUrl(page.slug) }];
  if (section) crumbs.push({ name: section.name(), url: siteUrl(page.slug, section.path) });
  if (service) crumbs.push({ name: service.name, url: siteUrl(page.slug, `/services/${service.id}`) });
  // A product's own page names the product, which this layout cannot know, so it adds its own trail.
  const isProduct = leaf?.routeId === "/(public)/$trainerSlug/shop/$productId/";

  return {
    meta: [
      ...pageMeta({ title: title ? `${title} · ${name}` : name, description, brand: false }),
      { property: "og:image", content: previewImage },
      { property: "og:image:alt", content: name },
    ],
    scripts: [
      ...(isHome ? [businessJsonLd({ page, business, directory, description, image })] : []),
      ...(service
        ? [
            serviceJsonLd({
              service,
              slug: page.slug,
              businessName: name,
              currency: directory.business.currency,
              description,
            }),
          ]
        : []),
      ...(section && !isProduct ? [breadcrumbJsonLd(crumbs)] : []),
    ],
  };
};

/**
 * One shop product's page: its own title, description and picture, and the product with its price
 * and whether it can be had, for search engines. The deepest route wins each meta name, so these
 * replace what the business's layout set.
 */
export const productPageHead = ({
  product,
  slug,
  businessName,
}: {
  product: ShopProduct;
  slug: string;
  businessName: string;
}): { meta: HeadMeta[]; scripts: HeadScript[] } => {
  const url = siteUrl(slug, `/shop/${product.id}`);
  const description =
    product.summary ||
    plainExcerpt(product.description) ||
    m.seo_service_description({ service: product.title, name: businessName });
  const image = product.images[0]?.url;

  return {
    meta: [
      ...pageMeta({ title: `${product.title} · ${businessName}`, description, brand: false }),
      ...(image
        ? [
            { property: "og:image", content: image },
            { property: "og:image:alt", content: product.title },
          ]
        : []),
    ],
    scripts: [
      jsonLdScript({
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.title,
        description,
        url,
        image: image ?? undefined,
        brand: { "@type": "Brand", name: businessName },
        offers: {
          "@type": "Offer",
          url,
          price: wholeAmount(product.priceAmount),
          priceCurrency: product.currency,
          availability: `https://schema.org/${product.soldOut ? "OutOfStock" : "InStock"}`,
          seller: { "@type": "Organization", name: businessName },
        },
      }),
      breadcrumbJsonLd([
        { name: businessName, url: siteUrl(slug) },
        { name: m.shop_title(), url: siteUrl(slug, "/shop") },
        { name: product.title, url },
      ]),
    ],
  };
};
